import { Check, ContentCopy, ErrorOutline } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/joy";
import { useEffect, useState } from "react";

type CopyState = "idle" | "copied" | "failed";

const TOOLTIPS: Record<CopyState, string> = {
  idle: "Copy to clipboard",
  copied: "Copied to clipboard!",
  failed: "Couldn't copy. Select the text instead.",
};

// Copies `value` exactly as given, so an identifier pastes unchanged into another system.
export default function CopyButton({ value, label }: { value: string; label: string }) {
  const [state, setState] = useState<CopyState>("idle");
  // Reset after a moment, and if the panel moves to another account first.
  useEffect(() => {
    if (state === "idle") return;
    const timeout = setTimeout(() => setState("idle"), 1500);
    return () => clearTimeout(timeout);
  }, [state]);

  return (
    <Tooltip title={TOOLTIPS[state]}>
      <IconButton
        size="sm"
        variant="plain"
        color={state === "failed" ? "danger" : "neutral"}
        aria-label={`Copy ${label}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setState("copied");
          } catch {
            setState("failed");
          }
        }}
        sx={{ "--IconButton-size": "22px" }}
      >
        {state === "copied" ? (
          <Check sx={{ fontSize: 14 }} />
        ) : state === "failed" ? (
          <ErrorOutline sx={{ fontSize: 14 }} />
        ) : (
          <ContentCopy sx={{ fontSize: 14 }} />
        )}
      </IconButton>
    </Tooltip>
  );
}
